import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule } from 'omnium-ui/dialog';
import { OuiTagModule } from 'omnium-ui/tag';
import { ActionAuthorizationService, doublonsBusterActionCodes } from '../../../../../../core/auth/action-authorization.service';
import {
  InvestorPurgeCheck,
  InvestorPurgeCheckItem,
  InvestorPurgeExecution,
} from '../../../../../../core/models/api.models';
import { InvestorCardComponent } from '../../../../../../core/components/investor-card/investor-card.component';
import { DoublonsBusterApiService } from '../../../../../../core/services/doublons-buster-api.service';
import { OuiIconModule } from 'omnium-ui/icon';

type InvestorPurgeCheckDialogData = {
  investorId: number;
  investorLabel: string;
};

@Component({
  selector: 'dbw-investor-purge-check-dialog',
  imports: [OuiButtonModule, OuiDialogModule, OuiTagModule, OuiIconModule, InvestorCardComponent],
  templateUrl: './investor-purge-check-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './investor-purge-check-dialog.component.scss',
})
export class InvestorPurgeCheckDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<InvestorPurgeCheckDialogComponent>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiService = inject(DoublonsBusterApiService);
  private readonly actionAuthorizationService = inject(ActionAuthorizationService);
  private readonly data = inject<InvestorPurgeCheckDialogData>(MAT_DIALOG_DATA);

  protected readonly investorId = this.data.investorId;
  protected readonly investorLabel = this.data.investorLabel;
  protected readonly loading = signal(true);
  protected readonly error = signal<string | undefined>(undefined);
  protected readonly result = signal<InvestorPurgeCheck | undefined>(undefined);
  protected readonly executing = signal(false);
  protected readonly executionError = signal<string | undefined>(undefined);
  protected readonly execution = signal<InvestorPurgeExecution | undefined>(undefined);
  protected readonly canExecutePurgeAction = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.investorPurgeExecute)
  );
  protected readonly canExecutePurge = computed(
    () =>
      this.canExecutePurgeAction() &&
      this.result()?.canPurge === true &&
      !this.loading() &&
      !this.executing() &&
      this.execution()?.isInProgress !== true
  );

  ngOnInit(): void {
    this.loadActiveExecution();
  }

  private loadActiveExecution(): void {
    this.loading.set(true);
    this.error.set(undefined);

    this.apiService
      .getActiveInvestorPurgeExecution(this.investorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: execution => {
          this.execution.set(execution);
          this.loading.set(false);
          this.result.set(undefined);
        },
        error: error => {
          if (error?.status === 404) {
            this.loadPurgeChecks();
            return;
          }

          this.error.set(error?.error?.detail ?? "Impossible de vérifier l'existence d'une purge en cours.");
          this.loading.set(false);
        },
      });
  }

  private loadPurgeChecks(): void {
    this.loading.set(true);
    this.error.set(undefined);

    this.apiService
      .checkInvestorPurge(this.investorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          this.result.set(result);
          this.loading.set(false);
        },
        error: error => {
          this.error.set(error?.error?.detail ?? 'Impossible de charger les contrôles de purge.');
          this.loading.set(false);
        },
      });
  }

  protected close(): void {
    this.dialogRef.close({ purgeStarted: this.execution()?.isInProgress === true });
  }

  protected executePurge(): void {
    if (!this.canExecutePurge()) {
      return;
    }

    this.executing.set(true);
    this.executionError.set(undefined);

    this.apiService
      .executeInvestorPurge(this.investorId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: execution => {
          this.executing.set(false);
          this.execution.set(execution);
          this.result.set(undefined);
        },
        error: error => {
          this.executing.set(false);
          this.executionError.set(error?.error?.detail ?? 'Impossible de demander la purge.');
        },
      });
  }

  protected getCheckStatus(check: InvestorPurgeCheckItem): 'completed' | 'todo' | 'error' {
    if (check.isOk) {
      return 'completed';
    }

    return check.severity === 'warning' ? 'todo' : 'error';
  }

  protected getCheckSeverityLabel(check: InvestorPurgeCheckItem): string {
    if (check.isOk) {
      return 'OK';
    }

    return check.severity === 'warning' ? 'Warning' : 'Bloquant';
  }

  protected hasFamilyRelations(check: InvestorPurgeCheckItem): boolean {
    return (check.familyRelations?.length ?? 0) > 0;
  }

  protected formatRelationType(relationTypeCode?: string | null): string {
    return relationTypeCode?.trim() || 'Relation familiale';
  }
}
